import React, { useMemo } from "react";

import {
  InspectableLineChart,
  Series,
  currencyFormatterNull,
} from "../../../../../components/Charts";
import {
  BREAKPOINTS,
  COLORS_GLOBAL,
  GRAPH_COLORS,
} from "../../../../../utils/constants";
import { useResponsive } from "../../../../../hooks/useResponsive";
import { SegmentedControl } from "../../../../../components/SegmentedControl";
import { DateFormat, formatDate } from "../../../../../utils";
import { ChartPoint } from "../../types";
import { progressAxisOf } from "./axis";
import { SavingProgressChartProps, LegendItem } from "./types";
import {
  ProgressCard,
  HeaderRow,
  TitleBlock,
  Title,
  Subtitle,
  LegendRow,
  LegendItemRow,
  LegendSwatch,
  EmptyMessage,
  DesktopControl,
  MobileControl,
} from "./styles";

const BALANCE_COLOR = GRAPH_COLORS[3];
const CONTRIBUTED_COLOR = COLORS_GLOBAL.gray500;

const MOBILE_CHART_HEIGHT = 300;
const DESKTOP_CHART_HEIGHT = 380;

const SavingProgressChartComponent: React.FC<SavingProgressChartProps> = ({
  points,
  window,
  options,
  onWindowChange,
  hasRetainedCashback,
}) => {
  const isMobile = useResponsive(BREAKPOINTS.tabletMin);
  const axis = useMemo(() => progressAxisOf(points), [points]);

  const chartData = useMemo(() => {
    if (points.length === 0) {
      return {
        series: [] as Series[],
        readoutSeries: [] as Series[],
        labels: [] as string[],
        readoutDates: [] as string[],
      };
    }

    const labels = points.map((point) => point.date);
    const readoutDates = points.map((point) =>
      formatDate(point.date, DateFormat.DMY_SPACE),
    );
    const netContributed: Series = {
      data: points.map((point) => point.netContributed),
      connectNulls: true,
      valueFormatter: currencyFormatterNull,
      label: "Net contributed",
      showMark: false,
      dashed: true,
      color: CONTRIBUTED_COLOR,
    };
    const balance: Series = {
      data: points.map((point) => point.balance),
      connectNulls: true,
      valueFormatter: currencyFormatterNull,
      label: "Balance",
      showMark: false,
      area: true,
      color: BALANCE_COLOR,
    };

    return {
      series: [netContributed, balance],
      readoutSeries: [balance, netContributed],
      labels,
      readoutDates,
    };
  }, [points]);

  const subtitle = hasRetainedCashback
    ? "The gap between the lines is retained interest and cashback."
    : "The gap between the lines is retained interest.";
  const chartHeight = isMobile ? MOBILE_CHART_HEIGHT : DESKTOP_CHART_HEIGHT;
  const hasData = chartData.labels.length > 0;
  const windowLabel =
    options.find((option) => option.value === window)?.label ?? window;

  const yMin = axis ? 0 : undefined;

  const legendItems: LegendItem[] = [
    { label: "Balance", color: BALANCE_COLOR, style: "solid" },
    { label: "Net contributed", color: CONTRIBUTED_COLOR, style: "dashed" },
  ];

  return (
    <ProgressCard aria-label="Saving progress chart">
      <HeaderRow>
        <TitleBlock>
          <Title>Saving progress</Title>
          <Subtitle>{subtitle}</Subtitle>
        </TitleBlock>
        <DesktopControl>
          <SegmentedControl
            options={options}
            value={window}
            onChange={onWindowChange}
            ariaLabel="Progress range"
            testId="saving-progress-filter"
          />
        </DesktopControl>
      </HeaderRow>

      <LegendRow>
        {legendItems.map((item) => (
          <LegendItemRow key={item.label}>
            <LegendSwatch $color={item.color} $style={item.style} />
            {item.label}
          </LegendItemRow>
        ))}
      </LegendRow>

      {hasData ? (
        <InspectableLineChart
          inspectLabel="Inspect saving progress by date"
          readoutDates={chartData.readoutDates}
          readoutSeries={chartData.readoutSeries}
          readoutTestId="saving-progress-readout"
          height={chartHeight}
          series={chartData.series}
          xLabels={chartData.labels}
          yMin={yMin}
          yMax={axis?.max}
          yTicks={axis?.ticks}
          hideLegend
          hideXAxisLine
          hideYAxisLine
          showHorizontalGrid
        />
      ) : (
        <EmptyMessage>
          No progress data yet for the {windowLabel} time frame.
        </EmptyMessage>
      )}

      <MobileControl>
        <SegmentedControl
          options={options}
          value={window}
          onChange={onWindowChange}
          ariaLabel="Progress range"
          testId="saving-progress-filter-mobile"
        />
      </MobileControl>
    </ProgressCard>
  );
};

export const SavingProgressChart = React.memo(SavingProgressChartComponent);
